import { DeleteOutlineRounded } from "@mui/icons-material";
import { Button, Chip, Divider, Skeleton, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  IssueActions,
  IssueContextProperty,
  IssueFeed,
  IssueProperties,
  ItemDescriptionField,
  LinkBehavior,
  Proposals,
  ShareButton,
  SignalRGroup,
} from "../../components";
import { Links } from "../../constants";
import { useSession, useSignalR } from "../../hooks";
import {
  queryKeys,
  useDeleteIssue,
  useEditIssue,
  useGetIssueDetail,
  useRemoveIssueUpvote,
  useUpvoteIssue,
} from "../../http";
import { setTitlePrefix, useDestructiveConfirm } from "../../utilities";
import { DetailPageLayout } from "./DetailPageLayout";

interface UrlParams {
  issueGuid: string;
}

const IssueDetailsPage = () => {
  const user = useSession();
  const { issueGuid } = useParams<keyof UrlParams>() as UrlParams;
  const { isLoading, data: issue } = useGetIssueDetail(issueGuid);
  const { mutate: deleteIssue } = useDeleteIssue();
  const { mutate: editIssue } = useEditIssue();
  const { mutate: upvoteIssue } = useUpvoteIssue();
  const { mutate: removeIssueUpvote } = useRemoveIssueUpvote();
  const { enqueueSnackbar } = useSnackbar();
  const [focusedProposalGuid, setFocusedProposalGuid] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const confirm = useDestructiveConfirm();
  const signalR = useSignalR();
  const queryClient = useQueryClient();

  useEffect(() => {
    return () => setFocusedProposalGuid(undefined);
  });

  useEffect(() => {
    setTitlePrefix(issue?.description);
  }, [issue?.description]);

  useEffect(() => {
    signalR.joinGroup(SignalRGroup.Issue(issueGuid));

    return () => {
      signalR.leaveGroup(SignalRGroup.Issue(issueGuid));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueGuid]);

  useEffect(() => {
    const handleShouldRefetchIssue = (issueGuid: string) => {
      queryClient.invalidateQueries(queryKeys.issues.getIssueDetail(issueGuid));
    };

    signalR.onShouldRefetchIssue(handleShouldRefetchIssue);
    return () => signalR.offShouldRefetchIssue(handleShouldRefetchIssue);
  }, [queryClient, signalR]);

  async function handleDescriptionChanged(value: string) {
    if (issue === undefined) {
      return;
    }

    editIssue({
      issueGuid: issue.guid,
      teamSlug: issue.assignedTeam?.slug,
      description: value,
      createdByUserId: issue.createdByUser.userId,
    });
  }

  async function handleUpvoteClicked() {
    if (issue === undefined) {
      return;
    }

    if (issue.upvotes.findIndex((x) => x.userId === user.userId) > -1) {
      removeIssueUpvote({
        issueGuid: issue.guid,
        userId: user.userId,
        teamSlug: issue.assignedTeam?.slug,
        createdByUserId: issue.createdByUser.userId,
      });
    } else {
      upvoteIssue({
        issueGuid: issue.guid,
        user: {
          userId: user.userId,
          displayName: user.displayName,
          enabled: true,
          profilePhotoSmallUrl: user.profilePhotoSmallUrl,
        },
        teamSlug: issue.assignedTeam?.slug,
        createdByUserId: issue.createdByUser.userId,
      });
    }
  }

  async function handleDeleteClicked(e: React.MouseEvent) {
    if (issue === undefined) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Delete Issue",
      content: "Are you sure you want to delete this issue?",
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteIssue(
      {
        issueGuid: issueGuid,
        teamSlug: issue.assignedTeam?.slug,
        createdByUserId: issue.createdByUser.userId,
      },
      {
        onSuccess: () => {
          navigate(-1);
          enqueueSnackbar(`Issue has been deleted`);
        },
      }
    );
  }

  return (
    <DetailPageLayout
      actions={
        <Stack direction="row" spacing={1}>
          <ShareButton variant="outlined" href={window.location.href} />
          <Divider orientation="vertical" />
          {issue?.userCanEditOtherProperties && (
            <Button variant="outlined" color="error" onClick={handleDeleteClicked} sx={{ minWidth: 18 }}>
              <DeleteOutlineRounded />
            </Button>
          )}
        </Stack>
      }
    >
      <Stack divider={<Divider />}>
        {isLoading && <Skeleton sx={{ mx: 1, mt: 0.5 }} width={"10%"} />}

        {!isLoading && issue && (
          <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ p: 1 }}>
            <Chip
              color="primary"
              component={LinkBehavior}
              href={
                issue.assignedTeam
                  ? Links.TeamImplementation(issue.assignedTeam.slug)
                  : Links.UserImplementation(issue.createdByUser.userId)
              }
              sx={{ textTransform: "none" }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              label={issue.assignedTeam ? issue.assignedTeam.name : issue.createdByUser.displayName}
              clickable
            />
            <Chip label="Issue" />
          </Stack>
        )}

        <Stack sx={{ px: 1 }} divider={<Divider variant="dashed" />}>
          {!isLoading && (issue?.context || issue?.userCanEditOtherProperties) && (
            <IssueContextProperty issue={issue} isEditable={issue.userCanEditOtherProperties} sx={{ py: 0.25 }} />
          )}

          <ItemDescriptionField
            isLoading={isLoading}
            isEditable={issue?.userCanEditTitle ?? false}
            value={issue?.description}
            onChange={handleDescriptionChanged}
            variant="h4"
            sx={{ py: 0.5 }}
          />

          <IssueActions
            isLoading={isLoading}
            issue={issue}
            onProposalCreated={(proposalGuid) => setFocusedProposalGuid(proposalGuid)}
            onUpvoteClicked={handleUpvoteClicked}
            sx={{ py: 0.5 }}
          />
          {!isLoading && issue && (
            <Proposals isLoading={isLoading} issue={issue} focusedProposalGuid={focusedProposalGuid} sx={{ py: 0.5 }} />
          )}
        </Stack>

        <IssueProperties issue={issue} isLoading={isLoading} />
        <IssueFeed issueGuid={issueGuid} />
      </Stack>
    </DetailPageLayout>
  );
};

export { IssueDetailsPage };
